import { IResult } from '../models/HttpResult';
import { IPagedResult } from '../models/HttpPagedResult';
import {
    SyncAnalysis,
    SyncAnalysisFinding,
    ISyncAnalysisFindingsFilters,
} from '../models/Entities/Protocols/SyncAnalysis';
import { PaginationState } from '../models/PaginationState';
import { buildHeaders, handleApiError } from './commons/CommonService.Functions';

const BaseURL =
    (process.env.REACT_APP_BILLER_API_GATEWAY || '').replace(/\/$/, '') +
    '/protocols/protocols/consumption-sync-analysis';

/** Cuántos hallazgos trae una página del listado si no se pide otro tamaño. */
export const SYNC_ANALYSIS_FINDINGS_DEFAULT_PAGE_SIZE = 20;

/**
 * Análisis de una corrida (GET .../by-sync-process/{syncProcessId}).
 *
 * Devuelve null cuando la corrida no tiene análisis: no haberlo analizado es un
 * estado normal, no un error que haya que mostrar como tal.
 */
export const GetProtocolConsumptionSyncAnalysis = async (
    syncProcessId: string,
    token: string
): Promise<SyncAnalysis | null> => {
    const url = `${BaseURL}/by-sync-process/${encodeURIComponent(syncProcessId)}`;
    try {
        const response = await fetch(url, { headers: buildHeaders(token) });
        if (response.status === 404) return null;
        if (!response.ok) throw response;
        const data: IResult<SyncAnalysis> = await response.json();
        return data.data ?? null;
    } catch (error: any) {
        throw await handleApiError(error, 'GetProtocolConsumptionSyncAnalysis');
    }
};

export interface SyncAnalysisFindingsPage {
    findings: SyncAnalysisFinding[];
    paginationState: PaginationState;
}

/**
 * Hallazgos del análisis (GET .../{analysisId}/findings).
 *
 * El servidor pagina y filtra: filtrar en el cliente sólo alcanzaría a la
 * página cargada y dejaría mal los totales y el recorrido de páginas.
 */
export const GetProtocolConsumptionSyncAnalysisFindings = async (
    analysisId: string,
    token: string,
    pageNumber: number = 1,
    pageSize: number = SYNC_ANALYSIS_FINDINGS_DEFAULT_PAGE_SIZE,
    filters?: ISyncAnalysisFindingsFilters
): Promise<SyncAnalysisFindingsPage> => {
    const query = new URLSearchParams();
    query.append('pageNumber', String(pageNumber));
    query.append('pageSize', String(pageSize));
    if (filters?.findingType !== undefined) query.append('findingType', String(filters.findingType));
    if (filters?.onlyNew) query.append('onlyNew', 'true');

    const url = `${BaseURL}/${encodeURIComponent(analysisId)}/findings?${query.toString()}`;

    try {
        const response = await fetch(url, { headers: buildHeaders(token) });
        if (!response.ok) throw response;
        const data: IPagedResult<SyncAnalysisFinding[]> = await response.json();
        return {
            findings: data.data || [],
            paginationState: {
                pageNumber: data.pageNumber,
                pageSize: data.pageSize,
                totalPages: data.totalPages,
                totalRecords: data.totalRecords,
                firstPage: data.firstPage,
                lastPage: data.lastPage,
                nextPage: data.nextPage,
                previousPage: data.previousPage,
            },
        };
    } catch (error: any) {
        throw await handleApiError(error, 'GetProtocolConsumptionSyncAnalysisFindings');
    }
};
